/**
 * PipelinePage — Architecture ETL GS-Apps (redesign 2026)
 * Vue pipeline Medallion : Sources → Bronze → Silver → Gold → Apps
 * Alimenté par GET /api/catalog — données live.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@gs/auth';
import { PageHeader } from '@gs/ui';
import type { TableDefinition } from '@gs/etl';
import { api } from '../services/api';

interface CatalogEntry extends TableDefinition {
  rowCount: number | null;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncRows: number | null;
}
interface CatalogResponse {
  ok: boolean;
  catalog: CatalogEntry[];
  total: number;
}

// ─── Helpers ──────────────────────────────────────────────────────

function fmt(n: number | null): string {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return n.toLocaleString('fr-CA');
  return String(n);
}

function statusDot(entry: CatalogEntry): { color: string; pulse: boolean; label: string } {
  if (!entry.isActive) return { color: 'bg-slate-300 dark:bg-slate-600', pulse: false, label: 'Planifié' };
  if (entry.lastSyncStatus === 'error') return { color: 'bg-red-500', pulse: false, label: 'Erreur' };
  if ((entry.rowCount ?? 0) > 0) return { color: 'bg-emerald-500', pulse: false, label: 'OK' };
  if (entry.lastSyncAt) return { color: 'bg-amber-400', pulse: false, label: 'Vide' };
  return { color: 'bg-amber-400', pulse: true, label: 'En attente' };
}

// ─── Composants atomiques ─────────────────────────────────────────

function StatusDot({ entry }: { entry: CatalogEntry }) {
  const { color, pulse } = statusDot(entry);
  return (
    <span className="relative flex h-2 w-2 shrink-0">
      {pulse && (
        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${color} opacity-75`} />
      )}
      <span className={`relative inline-flex rounded-full h-2 w-2 ${color}`} />
    </span>
  );
}

function Connector({ color = 'border-slate-200 dark:border-slate-700' }: { color?: string }) {
  return (
    <div className="flex items-center gap-0 shrink-0">
      <div className={`h-px w-8 border-t-2 ${color} border-dashed`} />
      <svg width="8" height="10" viewBox="0 0 8 10" className="shrink-0">
        <path
          d="M1 1L7 5L1 9"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-slate-300 dark:text-slate-600"
        />
      </svg>
    </div>
  );
}

// ─── Blocs de colonne ─────────────────────────────────────────────

function LayerHeader({
  label,
  sublabel,
  accent,
  count,
  total,
}: {
  label: string;
  sublabel: string;
  accent: string;
  count?: number;
  total?: string;
}) {
  return (
    <div className={`px-4 py-3 rounded-t-xl border-b ${accent}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
            {label}
          </div>
          <div className="text-meta text-slate-400 dark:text-slate-500 font-mono mt-0.5">{sublabel}</div>
        </div>
        {total && (
          <div className="text-right">
            <div className="text-sm font-bold text-slate-700 dark:text-slate-200 tabular-nums">{total}</div>
            {count !== undefined && <div className="text-chip text-slate-400">{count} tables</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function TableRow({ entry, prefix }: { entry: CatalogEntry; prefix: string }) {
  const short = entry.name.replace(prefix, '');
  const dot = statusDot(entry);

  const nativeTitle = [
    entry.name,
    entry.sofiTable ? `← SOFI: ${entry.sofiTable}` : '',
    entry.description,
    `${fmt(entry.rowCount)} rows`,
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <div
      className="relative group flex items-center gap-2 px-3 py-1.5 rounded-lg
                  hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-default"
      title={nativeTitle}
    >
      <StatusDot entry={entry} />

      <span className="flex-1 text-xs text-slate-600 dark:text-slate-300 font-mono truncate group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
        {short}
      </span>

      <span className="text-meta tabular-nums text-slate-400 dark:text-slate-500 shrink-0">{fmt(entry.rowCount)}</span>

      <div
        className="pointer-events-none absolute bottom-full left-0 mb-1.5 z-50
                    opacity-0 group-hover:opacity-100 transition-opacity duration-150"
      >
        <div className="bg-slate-900 dark:bg-slate-700 text-white rounded-lg shadow-xl px-3 py-2.5 min-w-52 max-w-field-lg text-left whitespace-nowrap">
          <div className="font-mono text-meta text-blue-300 mb-1 break-all whitespace-normal">{entry.name}</div>
          {entry.sofiTable && (
            <div className="text-chip text-amber-300 mb-1">← SOFI: {entry.sofiTable}</div>
          )}
          <div className="text-chip text-slate-400 leading-relaxed whitespace-normal">{entry.description || '—'}</div>
          <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-slate-700">
            <span className="text-chip text-slate-500">{dot.label}</span>
            <span className="text-chip tabular-nums text-slate-300 font-mono">{fmt(entry.rowCount)} rows</span>
          </div>
        </div>
        <div className="absolute top-full left-4 border-4 border-transparent border-t-slate-900 dark:border-t-slate-700" />
      </div>
    </div>
  );
}

// ─── Colonnes ─────────────────────────────────────────────────────

function SourcesColumn() {
  const sources = [
    {
      name: 'SOFI',
      sub: 'GS-SQL01 · NOLOCK',
      active: true,
      color: 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800',
    },
    {
      name: 'Azure AD',
      sub: 'Auth · login',
      active: true,
      color: 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800',
    },
    { name: 'Geotab', sub: 'GPS véhicules', active: false, color: 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700' },
    {
      name: 'JPL / ProFuel',
      sub: 'Carburant',
      active: false,
      color: 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700',
    },
    {
      name: 'SAAQ',
      sub: 'Immatriculations',
      active: false,
      color: 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700',
    },
    {
      name: 'Saisie manuelle',
      sub: 'Régions · inventaire',
      active: true,
      color: 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700',
    },
  ];

  return (
    <div className="flex flex-col gap-1.5 w-40 shrink-0">
      <div className="px-1 mb-1">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">Sources</div>
      </div>
      {sources.map((s) => (
        <div
          key={s.name}
          className={`rounded-lg border px-3 py-2 ${s.color} ${!s.active ? 'opacity-50' : ''}`}
        >
          <div className="flex items-center gap-1.5">
            <span
              className={`h-1.5 w-1.5 rounded-full shrink-0 ${s.active ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
            />
            <span className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{s.name}</span>
          </div>
          <div className="text-chip text-slate-400 dark:text-slate-500 mt-0.5 pl-3">{s.sub}</div>
        </div>
      ))}
      <div className="mt-2 px-1 space-y-0.5">
        {['WITH (NOLOCK)', 'truncateAndBulkLoad', 'TDS Bulk Copy'].map((t) => (
          <div key={t} className="text-chip text-slate-400 dark:text-slate-500 font-mono">
            {t}
          </div>
        ))}
      </div>
    </div>
  );
}

function BronzeColumn({ catalog }: { catalog: CatalogEntry[] }) {
  const [expanded, setExpanded] = useState(false);

  const staging = catalog.filter((e) => e.layer === 'STAGING' && e.isActive);
  const dim = catalog.filter((e) => e.layer === 'DIM');
  const stagingRows = staging.reduce((s, e) => s + (e.rowCount ?? 0), 0);
  const dimRows = dim.reduce((s, e) => s + (e.rowCount ?? 0), 0);
  const activeStaging = staging.filter((e) => (e.rowCount ?? 0) > 0).length;

  const keyStaging = [
    'ANL_STAGING_PROJETS',
    'ANL_STAGING_PLANS',
    'ANL_STAGING_LOGIMPRRDS',
    'ANL_STAGING_ESTIMATIONS',
    'ANL_STAGING_VEHICULE',
    'ANL_STAGING_BONMECANIQUEENTETE',
    'ANL_STAGING_FACTUREENTETE',
    'ANL_STAGING_USAGERS',
    'ANL_STAGING_CLIENT',
  ];
  const byName = new Map(catalog.map((e) => [e.name, e]));
  const errorCount = staging.filter((e) => e.lastSyncStatus === 'error').length;

  const allStaging = [...staging].sort((a, b) => (b.rowCount ?? 0) - (a.rowCount ?? 0));
  const displayedStaging = expanded ? allStaging : allStaging.filter((e) => keyStaging.includes(e.name));
  const hiddenCount = allStaging.length - displayedStaging.length;

  return (
    <div className="flex flex-col w-52 shrink-0">
      <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-white dark:bg-slate-900 shadow-xs">
        <LayerHeader
          label="Bronze"
          sublabel="ANL_STAGING_*"
          accent="bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800"
          count={activeStaging}
          total={fmt(stagingRows)}
        />
        <div className="p-2 space-y-0.5">
          {errorCount > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-red-50 dark:bg-red-950/20 mb-1">
              <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />
              <span className="text-meta text-red-600 dark:text-red-400">
                {errorCount} erreur{errorCount > 1 ? 's' : ''}
              </span>
            </div>
          )}
          {displayedStaging.map((e) => (
            <TableRow key={e.name} entry={e} prefix="ANL_STAGING_" />
          ))}
          {(hiddenCount > 0 || expanded) && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="w-full flex items-center gap-1.5 px-3 py-1.5 mt-1 rounded-lg
                         text-meta text-slate-400 dark:text-slate-500 hover:text-slate-600
                         dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50
                         transition-colors touch-manipulation"
            >
              <svg
                viewBox="0 0 10 10"
                className={`w-2.5 h-2.5 fill-none stroke-current stroke-[1.5] transition-transform ${expanded ? 'rotate-180' : ''}`}
              >
                <polyline points="2,3.5 5,6.5 8,3.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {expanded ? 'Réduire' : `+${hiddenCount} autres tables`}
            </button>
          )}
        </div>
        {/* DIM partagées */}
        <div className="border-t border-slate-100 dark:border-slate-800 bg-blue-50/50 dark:bg-blue-950/10 p-2">
          <div className="px-3 py-1 mb-1">
            <div className="text-chip font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
              DIM_* partagées
            </div>
            <div className="text-chip text-slate-400 dark:text-slate-500">{fmt(dimRows)} records · toutes les apps</div>
          </div>
          {[
            ['DIM_Succursale', '19'],
            ['DIM_Employees', '301'],
            ['DIM_Vehicules', '1 312'],
          ].map(([name, count]) => {
            const e = byName.get(name);
            return (
              <div key={name} className="flex items-center gap-2 px-3 py-1">
                <span
                  className={`h-1.5 w-1.5 rounded-full shrink-0 ${e && (e.rowCount ?? 0) > 0 ? 'bg-blue-500' : 'bg-slate-300'}`}
                />
                <span className="text-meta text-slate-600 dark:text-slate-300 flex-1 font-mono">
                  {name.replace('DIM_', '')}
                </span>
                <span className="text-meta tabular-nums text-slate-400">{count}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SilverColumn({ catalog }: { catalog: CatalogEntry[] }) {
  const [expandedDim, setExpandedDim] = useState(false);
  const [expandedFact, setExpandedFact] = useState(false);

  const dimAnl = catalog.filter((e) => e.layer === 'DIM_ANL');
  // FACT legacy conservé (Plans/Factures) + medallion SILVER tenant-scoped (ADR-056).
  const fact = catalog.filter((e) => (e.layer === 'FACT' || e.layer === 'SILVER') && e.isActive);
  const factRows = fact.reduce((s, e) => s + (e.rowCount ?? 0), 0);

  const keyDim = [
    'ANL_DIM_Succursale',
    'ANL_DIM_Projet',
    'ANL_DIM_Vehicule',
    'ANL_DIM_Client',
    'ANL_DIM_Usager',
    'ANL_DIM_Calendrier',
  ];
  const keyFact = [
    'silver.maintenance_orders',
    'silver.security_rounds',
    'fact.plans',
    'fact.invoices',
  ];

  const allDim = [...dimAnl].sort((a, b) => (b.rowCount ?? 0) - (a.rowCount ?? 0));
  const allFact = [...fact].sort((a, b) => (b.rowCount ?? 0) - (a.rowCount ?? 0));
  const displayedDim = expandedDim ? allDim : allDim.filter((e) => keyDim.includes(e.name));
  const displayedFact = expandedFact ? allFact : allFact.filter((e) => keyFact.includes(e.name));
  const hiddenDim = allDim.length - displayedDim.length;
  const hiddenFact = allFact.length - displayedFact.length;

  return (
    <div className="flex flex-col gap-3 w-52 shrink-0">
      {/* DIM ANL */}
      <div className="rounded-xl border border-teal-200 dark:border-teal-800 bg-white dark:bg-slate-900 shadow-xs">
        <LayerHeader
          label="Silver — DIM"
          sublabel="ANL_DIM_*"
          accent="bg-teal-50 dark:bg-teal-950/20 border-teal-200 dark:border-teal-800"
          count={dimAnl.length}
        />
        <div className="p-2 space-y-0.5">
          {displayedDim.map((e) => (
            <TableRow key={e.name} entry={e} prefix="ANL_DIM_" />
          ))}
          {(hiddenDim > 0 || expandedDim) && (
            <button
              type="button"
              onClick={() => setExpandedDim((v) => !v)}
              className="w-full flex items-center gap-1.5 px-3 py-1.5 mt-1 rounded-lg
                         text-meta text-slate-400 dark:text-slate-500 hover:text-slate-600
                         dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50
                         transition-colors touch-manipulation"
            >
              <svg
                viewBox="0 0 10 10"
                className={`w-2.5 h-2.5 fill-none stroke-current stroke-[1.5] transition-transform ${expandedDim ? 'rotate-180' : ''}`}
              >
                <polyline points="2,3.5 5,6.5 8,3.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {expandedDim ? 'Réduire' : `+${hiddenDim} autres tables`}
            </button>
          )}
        </div>
      </div>
      {/* FACT */}
      <div className="rounded-xl border border-teal-200 dark:border-teal-800 bg-white dark:bg-slate-900 shadow-xs">
        <LayerHeader
          label="Silver — FACT"
          sublabel="silver.* · fact.*"
          accent="bg-teal-50 dark:bg-teal-950/20 border-teal-200 dark:border-teal-800"
          count={fact.length}
          total={fmt(factRows)}
        />
        <div className="p-2 space-y-0.5">
          {displayedFact.map((e) => (
            <TableRow key={e.name} entry={e} prefix="fact." />
          ))}
          {(hiddenFact > 0 || expandedFact) && (
            <button
              type="button"
              onClick={() => setExpandedFact((v) => !v)}
              className="w-full flex items-center gap-1.5 px-3 py-1.5 mt-1 rounded-lg
                         text-meta text-slate-400 dark:text-slate-500 hover:text-slate-600
                         dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50
                         transition-colors touch-manipulation"
            >
              <svg
                viewBox="0 0 10 10"
                className={`w-2.5 h-2.5 fill-none stroke-current stroke-[1.5] transition-transform ${expandedFact ? 'rotate-180' : ''}`}
              >
                <polyline points="2,3.5 5,6.5 8,3.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {expandedFact ? 'Réduire' : `+${hiddenFact} autres tables`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function GoldColumn({ catalog }: { catalog: CatalogEntry[] }) {
  // Gold medallion tenant-scoped (ADR-056) + METRICS legacy (vide post-migration 80).
  const metrics = catalog.filter((e) => e.layer === 'GOLD' || e.layer === 'METRICS');

  return (
    <div className="flex flex-col gap-3 w-44 shrink-0">
      <div className="rounded-xl border border-green-200 dark:border-green-800 bg-white dark:bg-slate-900 shadow-xs">
        <LayerHeader
          label="Gold"
          sublabel="gold.*"
          accent="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800"
          count={metrics.filter((e) => e.isActive).length}
        />
        <div className="p-3 space-y-3">
          {metrics.map((e) => {
            const dot = statusDot(e);
            const short = e.name.replace('ANL_METRICS_', '').replace('gold.', '').replace(/_/g, ' ');
            return (
              <div
                key={e.name}
                className={`rounded-lg border p-2.5 transition-colors ${
                  e.isActive && (e.rowCount ?? 0) > 0
                    ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/10'
                    : 'border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30 opacity-60'
                }`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={`h-2 w-2 rounded-full shrink-0 ${dot.color}`} />
                  <span className="text-meta font-semibold text-slate-700 dark:text-slate-200 leading-tight">{short}</span>
                </div>
                <div className="text-lg font-bold tabular-nums text-slate-800 dark:text-slate-100 leading-none mb-1">
                  {fmt(e.rowCount)}
                </div>
                <div className="text-chip text-slate-400 dark:text-slate-500 leading-tight line-clamp-2">{e.description}</div>
                {!e.isActive && (
                  <div className="mt-1 text-chip text-slate-400 dark:text-slate-500 italic">Planifié</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AppsColumn() {
  const apps = [
    {
      name: 'GS-Admin',
      role: 'DIM_* + logs ETL',
      active: true,
      color: 'border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-950/10',
      text: 'text-violet-700 dark:text-violet-300',
    },
    {
      name: 'GS-Inventaire',
      role: 'DIM_Succursale',
      active: true,
      color: 'border-teal-200 dark:border-teal-800 bg-teal-50/50 dark:bg-teal-950/10',
      text: 'text-teal-700 dark:text-teal-300',
    },
    {
      name: 'GS-Flotte',
      role: 'DIM_* + Geotab',
      active: false,
      color: 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30',
      text: 'text-slate-500 dark:text-slate-400',
    },
    {
      name: 'GS-Ops',
      role: 'Planifié',
      active: false,
      color: 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30',
      text: 'text-slate-500 dark:text-slate-400',
    },
  ];

  return (
    <div className="flex flex-col gap-2 w-40 shrink-0">
      <div className="px-1 mb-1">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">Apps</div>
      </div>
      {apps.map((app) => (
        <div
          key={app.name}
          className={`rounded-lg border px-3 py-2.5 ${app.color} ${!app.active ? 'opacity-50' : ''}`}
        >
          <div className="flex items-center gap-1.5">
            <span
              className={`h-1.5 w-1.5 rounded-full shrink-0 ${app.active ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
            />
            <span className={`text-xs font-medium ${app.text} truncate`}>{app.name}</span>
          </div>
          <div className="text-chip text-slate-400 dark:text-slate-500 mt-0.5 pl-3">{app.role}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Barre orchestrateur ──────────────────────────────────────────

function OrchestratorBar() {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-950/10">
      <div className="h-7 w-7 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center shrink-0">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-violet-600 dark:text-violet-400">
          <rect x="1" y="1" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
          <rect x="8" y="1" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
          <rect x="1" y="8" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
          <path
            d="M10.5 8.5V13M10.5 13L8.5 11M10.5 13L12.5 11"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-violet-700 dark:text-violet-300">
          GS-Admin — Tour de contrôle ETL (ADR-016)
        </div>
        <div className="text-chip text-violet-500 dark:text-violet-500 font-mono mt-0.5 truncate">
          SyncManager → POST /api/syncs/:appId/inbound/run → runAnalyticsETL() @gs/etl → adm.SyncLogs
        </div>
      </div>
    </div>
  );
}

// ─── KPI bar ──────────────────────────────────────────────────────

function KpiBar({ catalog }: { catalog: CatalogEntry[] }) {
  const totalRows = catalog.reduce((s, e) => s + (e.rowCount ?? 0), 0);
  const activeLoaded = catalog.filter((e) => e.isActive && (e.rowCount ?? 0) > 0).length;
  const errorTables = catalog.filter((e) => e.lastSyncStatus === 'error').length;
  const lastSync = catalog
    .filter((e) => e.lastSyncAt)
    .sort((a, b) => new Date(b.lastSyncAt!).getTime() - new Date(a.lastSyncAt!).getTime())[0];

  const kpis: {
    label: string;
    value: string;
    sub?: string;
    color: string;
  }[] = [
    {
      label: 'Records chargés',
      value: fmt(totalRows),
      color: 'text-slate-800 dark:text-slate-100',
    },
    {
      label: 'Tables actives',
      value: `${activeLoaded}`,
      sub: `/ ${catalog.filter((e) => e.isActive).length}`,
      color: 'text-emerald-600 dark:text-emerald-400',
    },
    {
      label: 'Erreurs sync',
      value: String(errorTables),
      color: errorTables > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-400 dark:text-slate-500',
    },
    {
      label: 'Dernière sync',
      value: lastSync?.lastSyncAt
        ? new Date(lastSync.lastSyncAt).toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'short' })
        : '—',
      color: 'text-slate-600 dark:text-slate-300',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {kpis.map((kpi) => (
        <div
          key={kpi.label}
          className="rounded-xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3 shadow-xs"
        >
          <div className="text-meta text-slate-400 dark:text-slate-500 font-medium uppercase tracking-wider mb-1">
            {kpi.label}
          </div>
          <div className={`text-xl font-bold tabular-nums ${kpi.color} leading-none`}>
            {kpi.value}
            {kpi.sub && <span className="text-sm font-normal text-slate-400 ml-0.5">{kpi.sub}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────

export default function PipelinePage() {
  const { user } = useAuth();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'catalog'],
    queryFn: async () => {
      const r = await api.get<CatalogResponse>('/api/catalog');
      return r.catalog;
    },
    staleTime: 60_000,
    refetchOnMount: true,
    enabled: !!user,
  });

  const catalog = data ?? [];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Architecture ETL"
        description="Pipeline Medallion GS-Apps — Bronze → Silver → Gold"
        actions={
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isLoading}
            className="touch-manipulation text-xs px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors"
          >
            Actualiser
          </button>
        }
      />

      {isError && (
        <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-4 text-sm text-red-700 dark:text-red-400">
          Erreur de chargement.{' '}
          <button type="button" className="underline touch-manipulation" onClick={() => void refetch()}>
            Réessayer
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
            ))}
          </div>
          <div className="h-96 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
        </div>
      ) : (
        <>
          {/* KPIs */}
          <KpiBar catalog={catalog} />

          {/* Pipeline principal */}
          <div className="rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 p-4 overflow-x-auto">
            <div className="flex items-start gap-0 min-w-max">
              {/* Sources */}
              <SourcesColumn />

              {/* Connecteur Sources → Bronze */}
              <div className="flex items-center self-center mt-[-60px]">
                <Connector color="border-amber-300 dark:border-amber-700" />
              </div>

              {/* Bronze */}
              <BronzeColumn catalog={catalog} />

              {/* Connecteur Bronze → Silver */}
              <div className="flex items-center self-center mt-[-60px]">
                <Connector color="border-teal-300 dark:border-teal-700" />
              </div>

              {/* Silver */}
              <SilverColumn catalog={catalog} />

              {/* Connecteur Silver → Gold */}
              <div className="flex items-center self-center mt-[-80px]">
                <Connector color="border-green-300 dark:border-green-700" />
              </div>

              {/* Gold */}
              <GoldColumn catalog={catalog} />

              {/* Connecteur Gold → Apps */}
              <div className="flex items-center self-center mt-[-60px]">
                <Connector color="border-slate-300 dark:border-slate-600" />
              </div>

              {/* Apps */}
              <AppsColumn />
            </div>

            {/* Orchestrateur */}
            <div className="mt-4">
              <OrchestratorBar />
            </div>
          </div>

          {/* Légende couches */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              {
                label: 'Bronze',
                desc: 'Données brutes SOFI — TRUNCATE + full-load nightly',
                color:
                  'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/10 text-amber-800 dark:text-amber-200',
              },
              {
                label: 'Silver DIM',
                desc: 'Dimensions nettoyées — joins, normalisation',
                color: 'border-teal-200 dark:border-teal-800 bg-teal-50/50 dark:bg-teal-950/10 text-teal-800 dark:text-teal-200',
              },
              {
                label: 'Silver FACT',
                desc: 'Tables de faits — métriques par dimension',
                color: 'border-teal-200 dark:border-teal-800 bg-teal-50/50 dark:bg-teal-950/10 text-teal-800 dark:text-teal-200',
              },
              {
                label: 'Gold',
                desc: 'Agrégats pré-calculés — TCO, conformité, dashboards',
                color:
                  'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/10 text-green-800 dark:text-green-200',
              },
            ].map((item) => (
              <div key={item.label} className={`rounded-xl border px-4 py-3 ${item.color}`}>
                <div className="text-xs font-semibold uppercase tracking-wider mb-1">{item.label}</div>
                <div className="text-meta opacity-80 leading-relaxed">{item.desc}</div>
              </div>
            ))}
          </div>

          {/* Légende statuts */}
          <div className="flex items-center gap-6 px-1 flex-wrap">
            {[
              { color: 'bg-emerald-500', label: 'Actif et chargé' },
              { color: 'bg-red-500', label: 'Erreur dernière sync' },
              { color: 'bg-amber-400', label: 'Jamais synchronisé' },
              { color: 'bg-slate-300 dark:bg-slate-600', label: 'Planifié' },
            ].map(({ color, label }) => (
              <div key={label} className="flex items-center gap-1.5">
                <span className={`h-2 w-2 rounded-full ${color}`} />
                <span className="text-meta text-slate-500 dark:text-slate-400">{label}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
